import { UserMatterViewHistory } from '@models/userViewHistory';
import { Selector } from '@ngxs/store';
import { MergedMatterDocumentSelectors } from '@state/global/documents/merged-documents';
import {
	RecentlyViewedState,
	RecentlyViewedStateModel,
} from './recently-viewed.state';
import { Document, Folder } from '@models/matter';
import { MergedMatterFolderSelectors } from '@state/global/documents/merged-folders';
import { orderBy } from 'lodash';

export interface RecentlyViewedDocument extends Document {
	lastViewed: number;
	folderName: string;
}

export class RecentlyViewedSelector {
	@Selector([
		RecentlyViewedSelector.getCurrentMatterViewHistory,
		MergedMatterDocumentSelectors.getAll,
		MergedMatterFolderSelectors.getAll,
	])
	static getMatterViewedDocs(
		userMatterViewHistory: UserMatterViewHistory,
		documents: Document[],
		folders: Folder[],
	): RecentlyViewedDocument[] {
		if (!documents || documents.length === 0) {
			return [];
		}

		const viewedDocuments = userMatterViewHistory?.viewedDocuments ?? {};

		const docs = documents.map((x) => ({
			...x,
			lastViewed: Object.prototype.hasOwnProperty.call(viewedDocuments, x.id)
				? viewedDocuments[x.id]
				: 0,
			folderName:
				folders?.find((folder) => x.folderId === folder.id)?.title ?? '',
		}));

		return docs;
	}

	@Selector([RecentlyViewedState])
	static getAll(state: RecentlyViewedStateModel): UserMatterViewHistory[] {
		/**
		 * Prevent having JS do this sorting, do it in firestore
		 * Update firestore index to sort by lastViewed and utilize queryFn in state
		 * @see {@link RecentlyViewedState#getQueryConstraints}
		 */
		return orderBy(state.items ?? [], ['lastViewed'], ['desc']);
	}

	@Selector([RecentlyViewedSelector.getAll])
	static getTabs(items: UserMatterViewHistory[]): UserMatterViewHistory[] {
		const filtered = items.filter((x) => !x.hideFromTabs);
		const addedTabs = filtered.map((x) => ({
			...x,
			added: x?.added ?? x.lastViewed,
		}));

		const tabs = addedTabs.sort((a, b) => {
			if ((a.pinned && b.pinned) || !(a.pinned || b.pinned)) {
				return a.added - b.added;
			}

			if (a.pinned) {
				return -1;
			}

			if (b.pinned) {
				return 1;
			}

			return 0;
		});

		return tabs;
	}

	@Selector([RecentlyViewedState])
	static getCurrentMatterViewHistory(
		state: RecentlyViewedStateModel,
	): UserMatterViewHistory {
		return (
			state.items?.find((matter) => matter.id === state.currentMatterId) ?? null
		);
	}
}
